/**
 * CR225 Addendum: Payload Presence Indicators
 * 
 * Shows ✅/❌ for required payloads so you immediately know 
 * whether a missing badge is data-driven or UI-driven.
 */

import { useMemo } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, Info } from "lucide-react";
import { checkPayloadPresence, type PayloadPresenceResult } from "@/lib/deals/previewFixtures";
import type { RawDealInput } from "@/lib/deals/toUiDeal";
import type { UIDeal } from "@/lib/deals/toUiDeal";

// ================================================================
// TYPES
// ================================================================

interface PayloadIndicatorsProps {
  /** Current deal being previewed (can be RawDealInput or mapped UIDeal) */
  deal: RawDealInput | UIDeal | null;
  /** Compact mode for inline display */
  compact?: boolean;
}

// ================================================================
// COMPONENT
// ================================================================

export function PayloadIndicators({ deal, compact = false }: PayloadIndicatorsProps) {
  const presence = useMemo((): PayloadPresenceResult => {
    if (!deal) {
      return {
        hasRibbonPayload: false,
        hasStarburstPayload: false,
        hasDatesPayload: false,
        hasValuePayload: false,
        ribbonReason: "No deal loaded",
        starburstReason: "No deal loaded",
      };
    }
    
    // Map UIDeal fields back to check
    const rawInput: RawDealInput = {
      effective_percent_back: (deal as UIDeal).effective_percent_back ?? undefined,
      bonus_value_cad: (deal as UIDeal).bonus_value_cad ?? undefined,
      discount_percent: (deal as UIDeal).discount_percent ?? undefined,
      points_amount: (deal as UIDeal).points_amount ?? undefined,
      valid_from: (deal as UIDeal).start_date ?? undefined,
      valid_until: (deal as UIDeal).end_date ?? undefined,
    };
    
    return checkPayloadPresence(rawInput);
  }, [deal]);

  if (compact) {
    return (
      <div className="flex items-center gap-2 text-[10px]">
        <IndicatorBadge 
          label="R" 
          present={presence.hasRibbonPayload} 
          reason={presence.ribbonReason}
          fullLabel="Ribbon"
        />
        <IndicatorBadge 
          label="S" 
          present={presence.hasStarburstPayload} 
          reason={presence.starburstReason}
          fullLabel="Starburst"
        />
        <IndicatorBadge 
          label="D" 
          present={presence.hasDatesPayload} 
          reason={presence.hasDatesPayload ? "Has date fields" : "No dates in payload"}
          fullLabel="Dates"
        />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-1.5 p-2 bg-muted/30 rounded text-[10px]">
        <div className="flex items-center gap-1 text-muted-foreground font-medium">
          <Info className="h-3 w-3" />
          Payload Presence
        </div>
        
        <div className="flex flex-wrap gap-2">
          <IndicatorRow
            label="Ribbon"
            present={presence.hasRibbonPayload}
            reason={presence.ribbonReason}
          />
          <IndicatorRow
            label="Starburst"
            present={presence.hasStarburstPayload}
            reason={presence.starburstReason}
          />
          <IndicatorRow
            label="Dates"
            present={presence.hasDatesPayload}
            reason={presence.hasDatesPayload ? "Has valid_from or valid_until" : "No date fields in payload"}
          />
          <IndicatorRow
            label="Value"
            present={presence.hasValuePayload}
            reason={presence.hasValuePayload ? "Has value fields" : "No value fields (bonus, discount, points)"}
          />
        </div>
        
        {!presence.hasStarburstPayload && (
          <p className="text-muted-foreground italic">
            ⚠️ Starburst hidden: this deal has no badge payload.
          </p>
        )}
      </div>
    </TooltipProvider>
  );
}

// ================================================================
// HELPER COMPONENTS
// ================================================================

function IndicatorRow({ 
  label, 
  present, 
  reason 
}: { 
  label: string; 
  present: boolean; 
  reason: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="flex items-center gap-1 cursor-help">
          {present ? (
            <CheckCircle2 className="h-3 w-3 text-primary" />
          ) : (
            <XCircle className="h-3 w-3 text-muted-foreground" />
          )}
          <span className={present ? "text-foreground" : "text-muted-foreground"}>
            {label}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[250px]">
        <p className="text-xs">{reason}</p>
      </TooltipContent>
    </Tooltip>
  );
}

function IndicatorBadge({ 
  label, 
  fullLabel,
  present, 
  reason 
}: { 
  label: string;
  fullLabel: string;
  present: boolean; 
  reason: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge 
          variant={present ? "default" : "outline"} 
          className={`h-4 px-1.5 text-[9px] cursor-help ${
            present 
              ? "bg-primary/10 text-primary border-primary/30" 
              : "bg-muted text-muted-foreground border-muted-foreground/30"
          }`}
        >
          {present ? "✓" : "✗"} {label}
        </Badge>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[250px]">
        <p className="text-xs font-medium mb-1">{fullLabel} Payload</p>
        <p className="text-xs text-muted-foreground">{reason}</p>
      </TooltipContent>
    </Tooltip>
  );
}

export default PayloadIndicators;
